import { Trophy, ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";

interface LeagueFilterDropdownProps {
  selectedLeague: string;
  onLeagueChange: (leagueId: string) => void;
  leagues: Array<{
    id: string;
    name: string;
    short_name: string;
    api_football_id: number;
    logo_url?: string;
  }>;
}

export function LeagueFilterDropdown({
  selectedLeague,
  onLeagueChange,
  leagues,
}: LeagueFilterDropdownProps) {
  const [open, setOpen] = useState(false);

  const allLeagues = [
    { id: "all", name: "All Leagues", short_name: "All", api_football_id: 0, logo_url: undefined as string | undefined },
    ...leagues,
  ];

  const selected = allLeagues.find((l) => l.id === selectedLeague);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center gap-2 px-2 md:px-3 py-2 rounded text-sm font-bold",
            "bg-secondary text-secondary-foreground sketch-border hover-sketch",
            "transition-colors touch-manipulation h-10 min-h-[44px] md:h-8 md:min-h-auto"
          )}
        >
          {selected?.logo_url ? (
            <img src={selected.logo_url} alt={selected.short_name} className="w-5 h-5 object-contain" />
          ) : (
            <>
              <Trophy size={14} className="text-muted-foreground" />
              <span>{selected?.short_name || "All"}</span>
            </>
          )}
          <ChevronDown size={14} className="text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[260px] p-1 max-h-[360px] overflow-y-auto bg-card border-border"
      >
        {allLeagues.map((league) => (
          <button
            key={league.id}
            onClick={() => {
              onLeagueChange(league.id);
              setOpen(false);
            }}
            className={cn(
              "flex items-center justify-between w-full px-3 py-2 rounded text-sm font-medium text-left",
              "hover:bg-secondary transition-colors touch-manipulation",
              selectedLeague === league.id && "bg-secondary"
            )}
          >
            <div className="flex items-center gap-2">
              {league.logo_url ? (
                <img src={league.logo_url} alt={league.short_name} className="w-5 h-5 object-contain flex-shrink-0" />
              ) : (
                <Trophy size={16} className="text-muted-foreground flex-shrink-0" />
              )}
              <span>{league.short_name}</span>
            </div>
            {selectedLeague === league.id && (
              <Check size={14} className="text-primary flex-shrink-0" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
